import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { slugify } from '@/lib/utils';
import AuthShell from '@/components/auth/AuthShell';
import type { RegisterData } from '@/types';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  company_name: z.string().min(2, 'Company name is required'),
  slug: z
    .string()
    .min(3, 'Minimum 3 characters')
    .max(63, 'Maximum 63 characters')
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers, and hyphens only'),
});

export default function RegisterPage() {
  const { register: doRegister } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterData>({
    resolver: zodResolver(schema),
  });

  const companyName = watch('company_name');

  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue('company_name', value);
    setValue('slug', slugify(value).slice(0, 63));
  };

  const onSubmit = async (data: RegisterData) => {
    setServerError('');
    try {
      await doRegister(data);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setServerError(message);
    }
  };

  return (
    <AuthShell
      title="Start building"
      subtitle="Create your free account — no card required."
      altPrompt="Already have an account?"
      altCta="Sign in"
      altTo="/login"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {serverError && (
              <div className="flex items-start gap-2.5 rounded-lg border border-brand-error/30 bg-brand-error-soft px-3.5 py-2.5 text-sm text-brand-error">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{serverError}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="company_name">Company / Agency Name</Label>
              <Input
                id="company_name"
                placeholder="Smith Real Estate"
                {...register('company_name')}
                onChange={handleCompanyNameChange}
              />
              {errors.company_name && (
                <p className="text-xs text-brand-error">{errors.company_name.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="slug">Site URL</Label>
              <div className="flex items-center rounded-md border bg-brand-surface px-3">
                <Input
                  id="slug"
                  {...register('slug')}
                  className="border-0 bg-transparent px-0 focus-visible:ring-0"
                  placeholder="smith-realty"
                />
                <span className="flex-shrink-0 text-sm text-brand-ink-soft">
                  .{import.meta.env.VITE_ROOT_DOMAIN || 'searchproperties.io'}
                </span>
              </div>
              {errors.slug ? (
                <p className="text-xs text-brand-error">{errors.slug.message}</p>
              ) : (
                <p className="text-xs text-brand-ink-soft">
                  Your site will be at{' '}
                  <span className="font-mono">{watch('slug') || 'yoursite'}.{import.meta.env.VITE_ROOT_DOMAIN || 'searchproperties.io'}</span>
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-brand-error">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  {...register('password')}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ink-soft"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-brand-error">{errors.password.message}</p>
              )}
            </div>

            <Button type="submit" className="h-11 w-full text-sm" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create free account'
              )}
            </Button>

            <p className="text-center text-xs text-brand-ink-soft">
              By creating an account you agree to our{' '}
              <a href="#" className="underline">Terms</a> and{' '}
              <a href="#" className="underline">Privacy Policy</a>
            </p>
      </form>
    </AuthShell>
  );
}
